import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FloatingNavProps {
  activeTab: string;
  onTabChange: (tab: 'general' | 'self-assessment' | 'grow' | 'evaluators' | 'leadership' | 'career' | 'collaborator') => void;
  visibleTabs?: string[];
}

export function FloatingNav({
  activeTab,
  onTabChange,
  visibleTabs = ['general', 'self-assessment', 'evaluators', 'grow', 'leadership', 'career', 'collaborator']
}: FloatingNavProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollThreshold = 100;
      setIsVisible(window.scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md shadow-sm border-b border-gray-200"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="h-[50px] flex items-center">
              <div className="flex items-center px-4">
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/insightalks-47e16.firebasestorage.app/o/s4.svg?alt=media&token=c133b0b0-5054-4037-aa5f-668f41cf679b"
                  alt="InsighTalks"
                  className="h-8 w-auto"
                />
              </div>
              
              <div className="flex space-x-6 overflow-x-auto overflow-y-hidden hide-scrollbar pl-6 no-scrollbar">
                {visibleTabs.includes('general') && (
                  <TabButton 
                    label="General"
                    isActive={activeTab === 'general'}
                    onClick={() => onTabChange('general')}
                  />
                )}
                {visibleTabs.includes('self-assessment') && (
                  <TabButton 
                    label="Self-assessment"
                    isActive={activeTab === 'self-assessment'}
                    onClick={() => onTabChange('self-assessment')}
                  />
                )}
                {visibleTabs.includes('evaluators') && (
                  <TabButton 
                    label="Evaluators"
                    isActive={activeTab === 'evaluators'}
                    onClick={() => onTabChange('evaluators')}
                  />
                )}
                {visibleTabs.includes('grow') && (
                  <TabButton 
                    label="GROW"
                    isActive={activeTab === 'grow'}
                    onClick={() => onTabChange('grow')}
                  />
                )}
                {visibleTabs.includes('leadership') && (
                  <TabButton 
                    label="Leadership Edge"
                    isActive={activeTab === 'leadership'}
                    onClick={() => onTabChange('leadership')}
                  />
                )}
                {visibleTabs.includes('career') && (
                  <TabButton 
                    label="Career Accelerator"
                    isActive={activeTab === 'career'}
                    onClick={() => onTabChange('career')}
                  />
                )}
                {visibleTabs.includes('collaborator') && (
                  <TabButton 
                    label="Collaborator Genius"
                    isActive={activeTab === 'collaborator'}
                    onClick={() => onTabChange('collaborator')}
                  />
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface TabButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function TabButton({ label, isActive, onClick }: TabButtonProps) {
  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onClick();
  };

  return (
    <button
      onClick={handleClick}
      className={`group px-4 py-2 font-medium text-sm transition-all duration-200 relative rounded-lg ${getTabStyle(label, isActive)}`}
    >
      {label}
      <div 
        className={`absolute bottom-0 left-0 right-0 h-0.5 transition-all duration-200 transform origin-left ${
          isActive
            ? getActiveBarStyle(label)
            : getHoverBarStyle(label)
        }`}
      />
      <div 
        className={`absolute -bottom-[2px] left-1/2 w-1.5 h-1.5 rounded-full transition-all duration-200 transform -translate-x-1/2 ${
          isActive
            ? getDotStyle(label)
            : 'bg-primary/50 scale-0 opacity-0'
        }`}
      />
    </button>
  );
}

function getTabStyle(label: string, isActive: boolean): string {
  if (label === 'Leadership Edge' || 
      label === 'Career Accelerator' ||
      label === 'Collaborator Genius') {
    return isActive 
      ? 'text-[#f9c55f] bg-[#f9c55f]/5' 
      : 'text-gray-500 hover:text-gray-700';
  }
  return isActive 
    ? 'text-primary bg-primary/5' 
    : 'text-gray-500 hover:text-gray-700';
}

function getActiveBarStyle(label: string): string {
  if (label === 'Leadership Edge' || 
      label === 'Career Accelerator' ||
      label === 'Collaborator Genius') {
    return 'bg-[#f9c55f] scale-x-100 opacity-100';
  }
  return 'bg-primary scale-x-100 opacity-100';
}

function getHoverBarStyle(label: string): string {
  if (label === 'Leadership Edge' || 
      label === 'Career Accelerator' ||
      label === 'Collaborator Genius') {
    return 'bg-[#f9c55f]/50 scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-50';
  }
  return 'bg-primary/50 scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-50';
}

function getDotStyle(label: string): string {
  if (label === 'Leadership Edge' || 
      label === 'Career Accelerator' ||
      label === 'Collaborator Genius') {
    return 'bg-[#f9c55f] scale-100 opacity-100';
  }
  return 'bg-primary scale-100 opacity-100';
}